import React from "react"
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Tooltip from "react-bootstrap/Tooltip"
import type { BmdStrand } from "../types/bmd.types"

// Sentinel key for the synthetic "Unmapped topics" cluster (strand_id null),
// so the parent can track selections in a Set<number>.
export const BMD_UNMAPPED_STRAND_KEY = -1

export function strandKey(strand: Pick<BmdStrand, "strand_id">): number {
  return strand.strand_id ?? BMD_UNMAPPED_STRAND_KEY
}

// ---------------------------------------------------------------------------
// Design tokens, from the PM's mockup
// ---------------------------------------------------------------------------

const INK = "#1f2a44"
const BODY = "#5a6b8c"
const MUTED = "#8a97b1"
const RULE = "#eceff5"
const SELECTED_ROW_BG = "#f5f8fd"
const CHIP_BG = "#eef2f8"
const ACCENT = "#c62644"
const LOCKED_BG = "#f4f5f7"
/**
 * Text on a locked cluster row.
 *
 * Between BODY and INK: a locked row is most of the list on a Year 4 test, so
 * it has to be readable, but it should still sit a shade behind an available
 * one. The row is already marked locked three other ways - disabled checkbox,
 * no sub-strand chips, a Locked button instead of Select - so contrast is not
 * carrying that meaning on its own.
 */
const LOCKED_TEXT = "#44577a"

/**
 * The theme sizes a bare `input[type=checkbox]` to roughly 34px, which put a
 * large square next to 13px cluster names. Inline wins over the global rule
 * without a specificity fight, and the PM asked for small boxes explicitly.
 */
const CHECKBOX: React.CSSProperties = {
  width: 16,
  height: 16,
  margin: 0,
  accentColor: INK,
  flexShrink: 0,
}

/** One sub-strand name, as a pill. */
const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      backgroundColor: CHIP_BG,
      color: BODY,
      borderRadius: 20,
      padding: "2px 10px",
      fontSize: 11,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
)

type BmdStrandPickerProps = {
  strands: BmdStrand[]
  selectedStrandKeys: ReadonlySet<number>
  unlockedStrandKeys: ReadonlySet<number>
  lockedTopicIds: ReadonlySet<number>
  previousStrandNameByKey: ReadonlyMap<number, string>
  onToggleStrand: (key: number) => void
  onPreviewStrand?: (key: number, includedTopicIds: number[]) => void
}

/**
 * The cluster list on the Diagnostics assign tab.
 *
 * Three row states, as drawn:
 *   SELECTED   tinted row, ticked box, sub-strand chips, dark "Selected"
 *   AVAILABLE  white row, empty box, sub-strand chips, red "Select"
 *   LOCKED     greyed, no chips, a single disabled "Locked" button. The
 *              sub-strands collapse onto the name line as "Cluster — a, b, c",
 *              because a locked row is context rather than a choice and does
 *              not need to be scannable.
 *
 * Locked means the sequential unlock rule: clusters are worked through in
 * curriculum order, so one only opens when everything before it is either
 * already assigned or selected in this draft. The tooltip names the blocker.
 */
export function BmdStrandPicker({
  strands,
  selectedStrandKeys,
  unlockedStrandKeys,
  lockedTopicIds,
  previousStrandNameByKey,
  onToggleStrand,
  onPreviewStrand,
}: BmdStrandPickerProps) {
  if (strands.length === 0) {
    return (
      <p style={{ color: MUTED, fontSize: 12 }} className="mb-0">
        No diagnostic clusters available for the selected year level.
      </p>
    )
  }

  return (
    <ul className="list-unstyled mb-0">
      {strands.map((strand) => {
        const key = strandKey(strand)
        const isSelected = selectedStrandKeys.has(key)
        const isUnlocked = unlockedStrandKeys.has(key)
        const totalTopics = strand.topics.length
        const includedTopics = strand.topics.filter(
          (t) => !lockedTopicIds.has(t.main_topic_id),
        )
        const includedCount = includedTopics.length
        const lockedCount = totalTopics - includedCount
        // A cluster whose every topic is locked would assign zero topics.
        // Treat it as a soft lock.
        const allTopicsLocked = totalTopics > 0 && includedCount === 0
        const isDisabled = !isUnlocked || allTopicsLocked

        const includedQuestionCount = includedTopics.reduce(
          (sum, t) => sum + t.question_count,
          0,
        )
        // Sub-strand names, not topic names: topic names mostly repeat the
        // cluster's own name. The Unmapped bucket has no sub-strands, so it
        // falls back to topic names.
        const chipLabels =
          strand.sub_strand_names && strand.sub_strand_names.length > 0
            ? strand.sub_strand_names
            : includedTopics.map((t) => t.display_name)

        const inputId = `bmd-strand-${key}`
        const previousName = previousStrandNameByKey.get(key)

        const row = (
          <li
            key={key}
            style={{
              borderBottom: `1px solid ${RULE}`,
              backgroundColor: isSelected ? SELECTED_ROW_BG : undefined,
              padding: "12px 14px",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <input
                id={inputId}
                type="checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => {
                  if (!isDisabled) onToggleStrand(key)
                }}
                style={{
                  ...CHECKBOX,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
              />

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                {isDisabled ? (
                  // Locked: one line, name then sub-strands.
                  //
                  // Readable, not greyed into illegibility. The whole line used
                  // to sit at MUTED, which on a locked row is most of the list
                  // and was the thing the PM could not read. Locked is already
                  // said by the disabled checkbox and the Locked button, so it
                  // does not also need to be said by low contrast; the name
                  // just carries slightly less weight than an available one.
                  <div style={{ fontSize: 13, color: LOCKED_TEXT }}>
                    <span style={{ fontWeight: 600 }}>{strand.strand_name}</span>
                    {chipLabels.length > 0 ? (
                      <span>: {chipLabels.join(", ")}</span>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <label
                        htmlFor={inputId}
                        className="mb-0"
                        style={{
                          color: INK,
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {strand.strand_name}
                      </label>
                      {strand.previously_assigned ? (
                        <span
                          style={{
                            backgroundColor: CHIP_BG,
                            color: "#2f5fa8",
                            borderRadius: 20,
                            padding: "2px 9px",
                            fontSize: 11,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                          title="At least one topic in this cluster was assigned in a past diagnostics test"
                        >
                          <i
                            className="mdi mdi-check"
                            style={{ fontSize: 11 }}
                            aria-hidden="true"
                          ></i>{" "}
                          Added
                        </span>
                      ) : null}
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                      {chipLabels.map((name) => (
                        <Chip key={name}>{name}</Chip>
                      ))}
                    </div>
                    {lockedCount > 0 ? (
                      <div style={{ color: MUTED, fontSize: 11, marginTop: 6 }}>
                        {includedCount} of {totalTopics} topics included (
                        {lockedCount} locked until remediation)
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <span
                style={{
                  color: MUTED,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {includedQuestionCount} question
                {includedQuestionCount === 1 ? "" : "s"}
              </span>

              {/* Action pair. Locked collapses to a single dead button so the
                  column keeps its width and the rows stay on one grid. */}
              <div
                className="d-flex flex-shrink-0"
                style={{ width: 168, justifyContent: "flex-end" }}
              >
                {isDisabled ? (
                  <span
                    style={{
                      width: "100%",
                      textAlign: "center",
                      backgroundColor: LOCKED_BG,
                      color: MUTED,
                      border: `1px solid ${RULE}`,
                      borderRadius: 6,
                      padding: "7px 12px",
                      fontSize: 12.5,
                    }}
                  >
                    Locked
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onToggleStrand(key)}
                      style={{
                        backgroundColor: isSelected ? INK : ACCENT,
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px 0 0 6px",
                        padding: "7px 14px",
                        fontSize: 12.5,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isSelected ? (
                        <>
                          <i
                            className="mdi mdi-check"
                            style={{ fontSize: 12 }}
                            aria-hidden="true"
                          ></i>{" "}
                          Selected
                        </>
                      ) : (
                        "Select"
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={!onPreviewStrand}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onPreviewStrand?.(
                          key,
                          includedTopics.map((t) => t.main_topic_id),
                        )
                      }}
                      style={{
                        backgroundColor: "#fff",
                        color: ACCENT,
                        border: `1px solid ${ACCENT}`,
                        borderLeft: "none",
                        borderRadius: "0 6px 6px 0",
                        padding: "7px 14px",
                        fontSize: 12.5,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Preview
                    </button>
                  </>
                )}
              </div>
            </div>
          </li>
        )

        if (!isUnlocked) {
          const tooltipText = previousName
            ? `Complete or select "${previousName}" first.`
            : "Complete the previous cluster first."
          return (
            <OverlayTrigger
              key={key}
              placement="top"
              overlay={
                <Tooltip className="custom-tooltip">{tooltipText}</Tooltip>
              }
            >
              {row}
            </OverlayTrigger>
          )
        }
        if (allTopicsLocked) {
          return (
            <OverlayTrigger
              key={key}
              placement="top"
              overlay={
                <Tooltip className="custom-tooltip">
                  Every topic in this cluster is in progress or awaiting
                  remediation.
                </Tooltip>
              }
            >
              {row}
            </OverlayTrigger>
          )
        }

        return row
      })}
    </ul>
  )
}
